import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpRequest,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { Router } from '@angular/router';
import { MotifModal } from '@ey-xd/ng-motif';
import { MsalService } from '@azure/msal-angular';

import { AuthService } from '../auth/auth.service';
import { ErrorModalService } from 'src/app/shared/services/error-modal.service';
import { isErrorModalEnabledForRoute } from '../config/error-modal-policy';
import { environment } from 'src/environments/environment';

interface BackendErrorResponse {
  statusCode?: number;
  message?: string;
  correlationId?: string;
  internalCode?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ErrorHandlingInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private authService: AuthService,
    private authMsalService: MsalService,
    private errorModalService: ErrorModalService,
    private modal: MotifModal
  ) { }

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error instanceof HttpErrorResponse &&
          (error.status === 401 || error.status === 403) &&
          error.headers.get('x-error-source') === 'sharepoint') {
          return throwError(() => error);
        }
        const backendError = this.getBackendError(error);

        const statusCode = backendError.statusCode ?? error.status;

        const message =
          backendError.message ?? error.message ?? 'Unexpected error.';

        const correlationId = this.getCorrelationId(
          error,
          backendError,
          request
        );
        const internalCode = backendError.internalCode;
        const frontendRoute =
          request.headers.get('x-frontend-path')?.trim() || this.router.url;
        const isSharePointNotFound =
          statusCode === 404 && this.isSharePointRequest(request.url);

        if (
          statusCode >= 400 &&
          !isSharePointNotFound &&
          isErrorModalEnabledForRoute(frontendRoute)
        ) {
          this.errorModalService.show({
            statusCode,
            message,
            correlationId,
            method: request.method,
            url: error.url ?? request.url,
          });
        }

        if (error instanceof HttpErrorResponse && error.status === 403) {
          console.log('Error: 403 forbidden resource');

          this.redirectAfterAuthorizationError('ForbiddenResource');
        }

        if (error instanceof HttpErrorResponse && error.status === 401) {
          console.log('Error: 401 unauthorized');
          console.log(error);

          if (internalCode === '001') {
            console.log('401 - 001: User unregistered');

            this.redirectAfterAuthorizationError('Unregistered');
          } else if (internalCode === '002') {
            this.redirectAfterAuthorizationError('Unactive');
          } else if (internalCode === '003') {
            this.redirectAfterAuthorizationError('AlreadyLogged');
          } else if (internalCode === '004') {
            this.redirectAfterAuthorizationError('Unauthorized');
          } else {
            console.log('TRYING TO REFRESH');
            // this.refreshToken();
          }
        }

        if (error instanceof HttpErrorResponse && error.status === 400) {
          console.log(
            'Error: 400 There was an error with the url or the url params'
          );
        }

        return throwError(() => error);
      })
    );
  }

  private getBackendError(error: HttpErrorResponse): BackendErrorResponse {
    if (
      error.error &&
      typeof error.error === 'object' &&
      !(error.error instanceof ErrorEvent)
    ) {
      return error.error as BackendErrorResponse;
    }

    return {};
  }

  private isSharePointRequest(url: string): boolean {
    const backendBaseUrl = environment.baseUrl.endsWith('/')
      ? environment.baseUrl
      : `${environment.baseUrl}/`;
    return url.startsWith(`${backendBaseUrl}sharepoint/`);
  }

  private getCorrelationId(
    error: HttpErrorResponse,
    backendError: BackendErrorResponse,
    request: HttpRequest<unknown>
  ): string {
    return (
      this.nonEmptyValue(backendError.correlationId) ??
      this.nonEmptyValue(error.headers.get('x-correlation-id')) ??
      this.nonEmptyValue(request.headers.get('x-correlation-id')) ??
      '-'
    );
  }

  private nonEmptyValue(value: string | undefined | null): string | undefined {
    const normalizedValue = value?.trim();
    return normalizedValue || undefined;
  }

  refreshToken(): void {
    const refreshToken = JSON.parse(localStorage.getItem('refreshToken'));

    if (!refreshToken) {
      console.log('Retrieving token silent');
      localStorage.setItem('refreshToken', 'true');

      const request = {
        scopes: ['user.read', 'openid', 'profile', 'email'],
        account: this.authMsalService.instance.getAllAccounts()[0],
      };

      this.authMsalService.instance
        .ssoSilent(request)
        .then(async (result) => {
          if (result) {
            if (
              result.idToken === localStorage.getItem('idToken') ||
              !result.idToken ||
              !localStorage.getItem('idToken')
            ) {
              console.log('Equal tokens -> Redirect');
              this.loginRedirect();
            } else {
              console.log('Different tokens -> Token refreshed');

              this.authMsalService.instance.setActiveAccount(result.account);
              localStorage.setItem('idToken', result.idToken);

              const res = await this.loginTaxSuiteBackend();
              this.completeTokenRefresh(res);
            }
          }
        })
        .catch((error) => {
          console.log('SSO Error: ');
          console.log(error);

          localStorage.setItem('refreshToken', 'false');
          localStorage.setItem('loggedIn', 'false');

          this.loginRedirect();
        });
    } else {
      console.log('El ssoSilent ya se está ejecutando');
    }
  }

  loginRedirect(): void {
    void this.authService.loginRedirect().catch((error) => {
      console.error('Login redirect error:', error);
    });
  }

  async loginTaxSuiteBackend(): Promise<unknown> {
    return await this.authService
      .apiLogin({ lastLoginCountry: '' })
      .toPromise();
  }

  completeTokenRefresh(res): void {
    if (res && res.active) {
      localStorage.setItem('userLogged', JSON.stringify(res));
      localStorage.setItem('refreshToken', 'false');
    } else {
      this.redirectAfterAuthorizationError('Unauthorized');
    }
  }

  private redirectAfterAuthorizationError(errorType: string): void {
    this.modal.closeAll();
    this.router.navigate(['/tax-suite'], {
      queryParams: {
        errorType,
      },
    });
  }
}
